import React, { useEffect, useMemo, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Modal, Button, Form } from "react-bootstrap";
import { plansService } from "../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const emptyEdit = {
  id: null,
  code: "",
  name: "",
  price: "0",
  currency: "TRY",
  plan_interval: "monthly",
  interval_count: "1",
  trial_days: "0",
  notification_sms_limit: "0",
  notification_email_limit: "0",
  notification_whatsapp_limit: "0",
  file_storage_limit_gb: "0",
  enabled_modules_text: "[]",
  is_active: true,
};

function modulesToText(m) {
  if (m == null) return "[]";
  if (typeof m === "string") {
    try {
      return JSON.stringify(JSON.parse(m), null, 2);
    } catch {
      return m;
    }
  }
  return JSON.stringify(m, null, 2);
}

const PlansListPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [edit, setEdit] = useState(emptyEdit);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await plansService.getAllPlans();
      if (res.success) setPlans(res.data || []);
      else toast.error(res.message || res.error || "Planlar yüklenemedi");
    } catch (e) {
      toast.error("Planlar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let rows = plans || [];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((p) => {
        const blob = [
          p.code,
          p.name,
          String(p.id),
          p.plan_interval,
          p.currency,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
    }
    if (statusFilter === "active") {
      rows = rows.filter((p) => Number(p.is_active) === 1);
    } else if (statusFilter === "inactive") {
      rows = rows.filter((p) => Number(p.is_active) !== 1);
    }
    return rows;
  }, [plans, search, statusFilter]);

  const openEdit = (row) => {
    setEdit({
      id: row.id,
      code: row.code || "",
      name: row.name || "",
      price: String(row.price ?? "0"),
      currency: row.currency || "TRY",
      plan_interval: row.plan_interval || "monthly",
      interval_count: String(row.interval_count ?? 1),
      trial_days: String(row.trial_days ?? 0),
      notification_sms_limit: String(row.notification_sms_limit ?? 0),
      notification_email_limit: String(row.notification_email_limit ?? 0),
      notification_whatsapp_limit: String(row.notification_whatsapp_limit ?? 0),
      file_storage_limit_gb: String(row.file_storage_limit_gb ?? 0),
      enabled_modules_text: modulesToText(row.enabled_modules),
      is_active: Number(row.is_active) === 1,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEdit(emptyEdit);
  };

  const saveEdit = async () => {
    if (!edit.id) return;
    let modulesParsed;
    try {
      modulesParsed = JSON.parse(edit.enabled_modules_text || "[]");
      if (!Array.isArray(modulesParsed)) {
        toast.error("Modüller geçerli bir JSON dizi olmalıdır");
        return;
      }
    } catch {
      toast.error("Modül JSON'u geçersiz");
      return;
    }

    setSaving(true);
    const payload = {
      code: edit.code,
      name: edit.name,
      price: parseFloat(edit.price) || 0,
      currency: edit.currency,
      plan_interval: edit.plan_interval,
      interval_count: parseInt(edit.interval_count, 10) || 1,
      trial_days: parseInt(edit.trial_days, 10) || 0,
      notification_sms_limit: parseInt(edit.notification_sms_limit, 10) || 0,
      notification_email_limit: parseInt(edit.notification_email_limit, 10) || 0,
      notification_whatsapp_limit: parseInt(edit.notification_whatsapp_limit, 10) || 0,
      file_storage_limit_gb: parseInt(edit.file_storage_limit_gb, 10) || 0,
      enabled_modules: modulesParsed,
      is_active: edit.is_active ? 1 : 0,
    };

    const res = await plansService.updatePlan(edit.id, payload);
    setSaving(false);
    if (res.success) {
      toast.success("Plan güncellendi");
      closeModal();
      load();
    } else {
      toast.error(res.message || res.error || "Güncelleme başarısız");
    }
  };

  return (
    <MasterLayout>
      <ToastContainer />
      <Breadcrumb title="Planları Listele" />
      <div className="dashboard-main active">
        <div className="container-fluid">
          <div className="card mb-3">
            <div className="card-body d-flex flex-wrap gap-3 align-items-end">
              <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                <label className="form-label mb-1">Ara</label>
                <input
                  className="form-control"
                  placeholder="Kod, ad, aralık…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div style={{ minWidth: "160px" }}>
                <label className="form-label mb-1">Durum</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Tümü</option>
                  <option value="active">Aktif (vitrin)</option>
                  <option value="inactive">Pasif</option>
                </select>
              </div>
              <div className="text-muted small align-self-center">
                {filtered.length} / {plans.length} plan
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-0">
              {loading ? (
                <div className="p-4 text-center text-muted">Yükleniyor…</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Kod</th>
                        <th>Ad</th>
                        <th>Fiyat</th>
                        <th>Para</th>
                        <th>Aralık</th>
                        <th>x</th>
                        <th>Deneme gün</th>
                        <th>SMS</th>
                        <th>E-posta</th>
                        <th>WA</th>
                        <th>Depo GB</th>
                        <th>Vitrin</th>
                        <th>Modüller</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => (
                        <tr key={p.id}>
                          <td>{p.id}</td>
                          <td>
                            <code>{p.code}</code>
                          </td>
                          <td>{p.name}</td>
                          <td>{p.price}</td>
                          <td>{p.currency}</td>
                          <td>{p.plan_interval}</td>
                          <td>{p.interval_count}</td>
                          <td>{p.trial_days}</td>
                          <td>{p.notification_sms_limit}</td>
                          <td>{p.notification_email_limit}</td>
                          <td>{p.notification_whatsapp_limit}</td>
                          <td>{p.file_storage_limit_gb}</td>
                          <td>
                            {Number(p.is_active) === 1 ? (
                              <span className="badge bg-success">Aktif</span>
                            ) : (
                              <span className="badge bg-secondary">Pasif</span>
                            )}
                          </td>
                          <td
                            className="small text-truncate"
                            style={{ maxWidth: "140px" }}
                            title={modulesToText(p.enabled_modules)}
                          >
                            {modulesToText(p.enabled_modules).replace(/\s+/g, " ")}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => openEdit(p)}
                            >
                              Düzenle
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Plan düzenle #{edit.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Label>Kod</Form.Label>
              <Form.Control
                value={edit.code}
                onChange={(e) => setEdit({ ...edit, code: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Ad</Form.Label>
              <Form.Control
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Fiyat</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={edit.price}
                onChange={(e) => setEdit({ ...edit, price: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Para birimi</Form.Label>
              <Form.Control
                value={edit.currency}
                onChange={(e) => setEdit({ ...edit, currency: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Aralık</Form.Label>
              <Form.Select
                value={edit.plan_interval}
                onChange={(e) =>
                  setEdit({ ...edit, plan_interval: e.target.value })
                }
              >
                <option value="monthly">monthly</option>
                <option value="yearly">yearly</option>
                <option value="trial">trial</option>
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>Aralık çarpanı</Form.Label>
              <Form.Control
                type="number"
                value={edit.interval_count}
                onChange={(e) =>
                  setEdit({ ...edit, interval_count: e.target.value })
                }
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Deneme günü</Form.Label>
              <Form.Control
                type="number"
                value={edit.trial_days}
                onChange={(e) => setEdit({ ...edit, trial_days: e.target.value })}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <Form.Check
                type="switch"
                id="plan-active"
                label="Vitrinde aktif"
                checked={edit.is_active}
                onChange={(e) =>
                  setEdit({ ...edit, is_active: e.target.checked })
                }
              />
            </div>
            <div className="col-md-4">
              <Form.Label>SMS limit</Form.Label>
              <Form.Control
                type="number"
                value={edit.notification_sms_limit}
                onChange={(e) =>
                  setEdit({ ...edit, notification_sms_limit: e.target.value })
                }
              />
            </div>
            <div className="col-md-4">
              <Form.Label>E-posta limit</Form.Label>
              <Form.Control
                type="number"
                value={edit.notification_email_limit}
                onChange={(e) =>
                  setEdit({ ...edit, notification_email_limit: e.target.value })
                }
              />
            </div>
            <div className="col-md-4">
              <Form.Label>WhatsApp limit</Form.Label>
              <Form.Control
                type="number"
                value={edit.notification_whatsapp_limit}
                onChange={(e) =>
                  setEdit({
                    ...edit,
                    notification_whatsapp_limit: e.target.value,
                  })
                }
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Dosya depolama (GB)</Form.Label>
              <Form.Control
                type="number"
                value={edit.file_storage_limit_gb}
                onChange={(e) =>
                  setEdit({ ...edit, file_storage_limit_gb: e.target.value })
                }
              />
            </div>
            <div className="col-12">
              <Form.Label>enabled_modules (JSON dizi)</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                className="font-monospace small"
                value={edit.enabled_modules_text}
                onChange={(e) =>
                  setEdit({ ...edit, enabled_modules_text: e.target.value })
                }
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            İptal
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={saving}>
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </Modal.Footer>
      </Modal>
    </MasterLayout>
  );
};

export default PlansListPage;
